/**
 * Validates if input is a URL using omnibox-style detection rules
 * Similar to Chrome's omnibox behavior for distinguishing URLs from search queries
 *
 * @param input - The user input to validate
 * @returns true if the input is likely a URL, false if it's likely a search query
 *
 * @example
 * isValidUrl('google.com') // true
 * isValidUrl('http://example.com') // true
 * isValidUrl('localhost:3000') // true
 * isValidUrl('192.168.1.1') // true
 * isValidUrl('search term') // false
 * isValidUrl('how to cook pasta') // false
 */
export function isValidUrl(input: string): boolean {
  if (!input || typeof input !== 'string') {
    return false
  }

  const trimmed = input.trim()

  // Empty or whitespace-only input is not a URL
  if (trimmed.length === 0) {
    return false
  }

  // Explicit protocol indicates a URL
  if (/^(https?|ftp|file):\/\//i.test(trimmed)) {
    return true
  }

  // localhost variations (with or without port)
  if (/^localhost(:\d+)?(\/|$)/i.test(trimmed)) {
    return true
  }

  // IP addresses (IPv4)
  const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}(:\d+)?(\/|$)/
  if (ipv4Pattern.test(trimmed)) {
    // Validate octets are in range 0-255
    const parts = trimmed.split(/[:/]/)[0].split('.')
    if (
      parts.every(
        (part) => parseInt(part, 10) >= 0 && parseInt(part, 10) <= 255
      )
    ) {
      return true
    }
  }

  // IPv6 addresses (requires proper structure with multiple colons)
  // Must have at least 2 colons and proper hex segments (0-4 hex chars per segment)
  // Matches formats like: [2001:db8::1], ::1, 2001:0db8:85a3::8a2e:0370:7334
  const ipv6Pattern = /^\[?([0-9a-f]{0,4}:){2,7}[0-9a-f]{0,4}\]?(:\d+)?(\/|$)/i
  if (ipv6Pattern.test(trimmed)) {
    return true
  }

  // Check for invalid characters in the domain part (before any path/query/fragment)
  // Valid characters: a-z, 0-9, hyphens, dots, colons (ports), brackets (IPv6), unicode
  // Invalid: apostrophes, quotes, most special chars that don't belong in domains
  const domainPart = trimmed.split(/[/?#]/)[0] // Get domain before path, query, or fragment
  if (/['"`<>{}|\\^~]/.test(domainPart)) {
    return false
  }

  // Multiple words separated by spaces are likely a search query
  if (/\s+/.test(trimmed) && !/^[^\s]+\s*\//.test(trimmed)) {
    return false
  }

  // Single word without spaces or special URL indicators
  // Check if it contains a dot and looks domain-like
  if (!trimmed.includes(' ') && trimmed.includes('.')) {
    // Check for common TLDs first
    const commonTLDs =
      /\.(com|org|net|edu|gov|mil|co|io|ai|dev|app|tech|info|biz|me|tv|xyz|online|site|uk|ca|au|de|fr|jp|cn|br|vn|in|ru|es|mx|it|kr|nl)(:\d+)?(\/|$)/i
    if (commonTLDs.test(trimmed)) {
      return true
    }

    // Has URL path, query, or fragment indicators
    if (/[/?#]/.test(trimmed)) {
      // But not if it looks like a file path (has file extension at the end)
      const fileExtensions =
        /\.(txt|pdf|doc|docx|xls|xlsx|jpg|jpeg|png|gif|zip|tar|gz)$/i
      if (fileExtensions.test(trimmed)) {
        return false
      }
      return true
    }
  }

  // Check for TLD patterns (domain-like structure)
  // Pattern: something.tld or subdomain.domain.tld (with optional port, path, query, fragment)
  // But exclude file-like patterns
  const domainPattern =
    /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/
  if (domainPattern.test(trimmed)) {
    // Exclude patterns that look like files
    const fileExtensions =
      /\.(txt|pdf|doc|docx|xls|xlsx|jpg|jpeg|png|gif|zip|tar|gz)$/i
    if (fileExtensions.test(trimmed.split(/[/:?#]/)[0])) {
      return false
    }
    return true
  }

  // Internationalized domain names (basic check for non-ASCII characters)
  if (
    trimmed.includes('.') &&
    [...trimmed].some((char) => char.charCodeAt(0) > 127)
  ) {
    return true
  }

  // Default: treat as search query
  return false
}

/**
 * Normalizes a URL input by adding protocol if missing
 *
 * @param input - The URL input to normalize
 * @returns Normalized URL with protocol
 *
 * @example
 * normalizeUrl('google.com') // 'https://google.com'
 * normalizeUrl('http://example.com') // 'http://example.com'
 */
export function normalizeUrl(input: string): string {
  const trimmed = input.trim()

  // Already has protocol
  if (/^(https?|ftp|file):\/\//i.test(trimmed)) {
    return trimmed
  }

  // Add https:// for protocol-less URLs
  return `https://${trimmed}`
}
